import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';

const MAX_WIDTH = 800;
const MAX_HEIGHT = 800;
const QUALITY = 0.85;

/**
 * Resizes an image file to max dimensions via canvas and returns a base64 data URL.
 * @param {File} file
 * @returns {Promise<string>} base64 data URL
 */
function resizeToBase64(file) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);

        img.onload = () => {
            URL.revokeObjectURL(url);

            let { width, height } = img;

            // Scale down proportionally if needed
            if (width > MAX_WIDTH || height > MAX_HEIGHT) {
                const ratio = Math.min(MAX_WIDTH / width, MAX_HEIGHT / height);
                width = Math.round(width * ratio);
                height = Math.round(height * ratio);
            }

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);

            resolve(canvas.toDataURL(file.type === 'image/png' ? 'image/png' : 'image/jpeg', QUALITY));
        };

        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Image load failed'));
        };

        img.src = url;
    });
}

/**
 * PasteImageExtension — intercepts paste events containing images.
 * Resizes to max 800x800px and inserts as inline base64 <img>.
 */
export const PasteImageExtension = Extension.create({
    name: 'pasteImage',

    addProseMirrorPlugins() {
        const editor = this.editor;

        return [
            new Plugin({
                key: new PluginKey('pasteImage'),
                props: {
                    handlePaste(view, event) {
                        const items = Array.from(event.clipboardData?.items ?? []);
                        const imageItem = items.find((item) => item.type.startsWith('image/'));

                        if (!imageItem) return false;

                        event.preventDefault();

                        const file = imageItem.getAsFile();
                        if (!file) return true;

                        resizeToBase64(file).then((dataUrl) => {
                            editor.chain().focus().insertContent(`<img src="${dataUrl}" />`).run();
                        });

                        return true;
                    },
                },
            }),
        ];
    },
});
